import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, Clock, Package, FileText, Rocket, Mail, 
  Send, Calendar, X, ListTodo, MessageCircle, Upload, 
  ExternalLink, LogOut, Sparkles, ArrowRight, ChevronRight, 
  Star, Menu, TrendingUp, Zap, Target, Award, ArrowUpRight,
  CheckSquare, LayoutGrid, MessageSquare, FolderOpen, Play,
  CalendarPlus, Video
} from 'lucide-react';
import InvoiceModal from '../components/InvoiceModal';
import SimpleChatThread from '../components/SimpleChatThread';
import FilesList from '../components/FilesList';
import FileUpload from '../components/FileUpload';
import '../styles/chat.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Jotform links
const JOTFORM_CHECKIN_URL = 'https://form.jotform.com/253154590703051';
const JOTFORM_ASSETS_URL = 'https://form.jotform.com/253155268581058';

// Stage configuration
const STAGES = [
  { key: 'INTAKE', label: 'Intake', icon: Package },
  { key: 'COPY_DRAFT', label: 'Copy', icon: FileText },
  { key: 'DESIGN', label: 'Design', icon: Sparkles },
  { key: 'DEV', label: 'Build', icon: Rocket },
  { key: 'QA', label: 'Review', icon: CheckSquare },
  { key: 'LAUNCHED', label: 'Live', icon: Star }
];

// Helper
const formatNiceDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return null; }
};

// Animated Progress Bar
const ProgressBar = ({ progress }) => (
  <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${progress}%` }}
      transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }}
      className="absolute inset-y-0 left-0 rounded-full"
      style={{
        background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 35%, #EC4899 70%, #F59E0B 100%)'
      }}
    />
    {/* Shimmer effect */}
    <motion.div
      initial={{ x: '-100%' }}
      animate={{ x: '200%' }}
      transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
      className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent"
    />
  </div>
);

// Stage Timeline - Clean horizontal design
const StageTimeline = ({ currentStage }) => {
  const currentIndex = STAGES.findIndex(s => s.key === currentStage) || 0;

  return (
    <div className="flex items-center justify-between w-full">
      {STAGES.map((stage, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        const Icon = stage.icon;

        return (
          <React.Fragment key={stage.key}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="flex flex-col items-center"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                className={`relative w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 ${
                  isCompleted 
                    ? 'bg-gradient-to-br from-emerald-400 to-green-500 text-white shadow-lg shadow-green-200/50' 
                    : isCurrent 
                      ? 'bg-white text-indigo-600 shadow-xl shadow-indigo-200/50 ring-2 ring-indigo-500 ring-offset-2' 
                      : 'bg-gray-100 text-gray-400'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle2 size={18} strokeWidth={2.5} />
                ) : (
                  <Icon size={18} />
                )}
                {isCurrent && (
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute -top-1 -right-1 w-3 h-3 bg-indigo-500 rounded-full"
                  />
                )}
              </motion.div>
              <span className={`mt-2 text-xs font-semibold tracking-wide ${
                isCurrent ? 'text-indigo-600' : isCompleted ? 'text-emerald-600' : 'text-gray-400'
              }`}>
                {stage.label}
              </span>
            </motion.div>
            
            {/* Connector line */}
            {index < STAGES.length - 1 && (
              <div className="flex-1 h-0.5 mx-2 rounded-full overflow-hidden bg-gray-200">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: isCompleted ? '100%' : '0%' }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="h-full bg-gradient-to-r from-emerald-400 to-green-500"
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Mobile Stage Timeline
const MobileStageTimeline = ({ currentStage }) => {
  const currentIndex = STAGES.findIndex(s => s.key === currentStage) || 0;
  const currentStageData = STAGES[currentIndex];
  const Icon = currentStageData.icon;

  return (
    <div className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-lg">
        <Icon size={22} />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-500">Current Stage</p>
        <p className="text-lg font-bold text-gray-900">{currentStageData.label}</p>
      </div>
      <div className="text-right">
        <p className="text-sm text-gray-500">Step</p>
        <p className="text-lg font-bold text-indigo-600">{currentIndex + 1}/{STAGES.length}</p>
      </div>
    </div>
  );
};

// Bento Stat Card
const BentoCard = ({ icon: Icon, label, value, gradient, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    whileHover={{ y: -4, scale: 1.02 }}
    className="relative overflow-hidden rounded-2xl p-5"
    style={{ background: gradient }}
  >
    <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px)`,
      backgroundSize: '20px 20px'
    }} />
    <div className="relative z-10">
      <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-3">
        <Icon size={20} className="text-white" />
      </div>
      <p className="text-white/80 text-sm mb-1">{label}</p>
      <p className="text-white text-2xl font-bold">{value}</p>
    </div>
  </motion.div>
);

// Quick Action Card
const ActionCard = ({ icon: Icon, title, description, onClick, gradient, external }) => (
  <motion.button
    whileHover={{ scale: 1.02, y: -2 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="w-full p-4 rounded-2xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 text-left group"
  >
    <div className="flex items-start gap-4">
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: gradient }}
      >
        <Icon size={22} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors flex items-center gap-2">
          {title}
          {external ? (
            <ArrowUpRight size={14} className="text-gray-400 group-hover:text-indigo-500" />
          ) : (
            <ChevronRight size={14} className="text-gray-400 group-hover:translate-x-1 transition-transform" />
          )}
        </p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  </motion.button>
);

// Todo Item
const TodoItem = ({ todo, onToggle, index }) => {
  const isCompleted = todo.status === 'completed';

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      onClick={() => !isCompleted && onToggle(todo.id)}
      className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200 ${
        isCompleted 
          ? 'bg-gray-50 opacity-60' 
          : 'bg-gradient-to-r from-white to-gray-50 border border-gray-100 hover:border-indigo-200 hover:shadow-md'
      }`}
    >
      <motion.div
        whileHover={!isCompleted ? { scale: 1.1 } : {}}
        whileTap={!isCompleted ? { scale: 0.9 } : {}}
        className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isCompleted 
            ? 'bg-gradient-to-br from-green-400 to-emerald-500' 
            : 'border-2 border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50'
        }`}
      >
        {isCompleted && <CheckCircle2 size={14} className="text-white" />}
      </motion.div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
          {todo.title}
        </p>
        {todo.due_date && !isCompleted && (
          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
            <Clock size={12} /> {formatNiceDate(todo.due_date)}
          </p>
        )}
      </div>
    </motion.div>
  );
};

// Main Component
const ClientDashboardPageV4 = () => {
  const navigate = useNavigate();
  const { clientId: urlClientId } = useParams();
  const [clientData, setClientData] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  
  // Scheduling state
  const [meetings, setMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submittingMeeting, setSubmittingMeeting] = useState(false);

  useEffect(() => {
    document.title = 'Client Dashboard | BM-Assist';
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchClientData = async () => {
    if (!urlClientId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${urlClientId}`, { credentials: 'include' });
      if (res.status === 401) { navigate(`/client-login`); return; }
      if (res.ok) { const data = await res.json(); setClientData(data); }
      else { showToast('Error fetching client data', 'error'); }
    } catch (err) {
      console.error('Error:', err);
      showToast('Error loading dashboard', 'error');
    } finally { setIsLoading(false); }
  };

  useEffect(() => {
    if (urlClientId) fetchClientData();
    else navigate('/client-login');
  }, [urlClientId]);

  const onToggleTodo = async (todoId) => {
    if (!todoId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${clientData.client_id}/todos/${todoId}/toggle?actor=client`, {
        method: 'PATCH', credentials: 'include'
      });
      const data = await res.json();
      if (data.ok && data.todo) {
        setClientData(prev => ({
          ...prev,
          client_todos: (prev.client_todos || []).map(t => t.id === todoId ? data.todo : t),
          activity_log: [data.activity, ...(prev.activity_log || [])]
        }));
        showToast(data.todo.completed ? '✓ Task completed!' : 'Task reopened');
      }
    } catch (err) { showToast('Failed to update task', 'error'); }
  };

  const handleMessagePM = () => {
    // Switch to messages tab instead of opening email
    setActiveTab('messages');
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/client/logout`, { method: 'POST', credentials: 'include' });
    } catch (err) {}
    navigate('/client-login');
  };

  // Fetch meetings for client
  const fetchMeetings = async () => {
    if (!clientData?.client_id) return;
    setLoadingMeetings(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${clientData.client_id}/meetings`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setMeetings(data.meetings || []);
      }
    } catch (err) {
      console.error('Error fetching meetings:', err);
    } finally {
      setLoadingMeetings(false);
    }
  };

  // Fetch available slots for a date
  const fetchAvailableSlots = async (date) => {
    if (!date || !clientData?.client_id) return;
    
    setLoadingSlots(true);
    setAvailableSlots([]);
    setSelectedTime('');
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${clientData.client_id}/available-slots?date=${date}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data.slots || []);
        if (data.slots?.length === 0) {
          showToast('No available slots on this day', 'error');
        }
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Handle date selection
  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchAvailableSlots(date);
  };

  // Submit meeting request
  const handleSubmitMeeting = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      showToast('Please select a date and time', 'error');
      return;
    }

    setSubmittingMeeting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${clientData.client_id}/meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: selectedDate,
          time: selectedTime,
          notes: meetingNotes
        })
      });

      if (res.ok) {
        showToast('Check-in meeting scheduled!');
        setShowScheduleModal(false);
        setSelectedDate('');
        setSelectedTime('');
        setMeetingNotes('');
        setAvailableSlots([]);
        fetchMeetings();
      } else {
        const data = await res.json();
        showToast(data.detail || 'Failed to schedule meeting', 'error');
      }
    } catch (err) {
      console.error('Error submitting meeting:', err);
      showToast('Error scheduling meeting', 'error');
    } finally {
      setSubmittingMeeting(false);
    }
  };

  // Fetch meetings when tab changes to schedule
  useEffect(() => {
    if (activeTab === 'schedule' && clientData) {
      fetchMeetings();
    }
  }, [activeTab, clientData]);

  // Metrics
  const todos = clientData?.client_todos || [];
  const openTasks = todos.filter(t => t.status !== 'completed').length;
  const completedTasks = todos.filter(t => t.status === 'completed').length;
  const milestonesTotal = clientData?.milestones?.length || 0;
  const milestonesDone = clientData?.milestones?.filter(m => m.status === 'completed')?.length || 0;
  const filesCount = clientData?.files?.length || 0;

  // Loading
  if (isLoading || !clientData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-4"
          />
          <p className="text-gray-600 font-medium">Loading your dashboard...</p>
        </motion.div>
      </div>
    );
  }

  const currentStage = clientData.status_stage || 'INTAKE';
  const progressPercent = clientData.progress_percent || 0;
  const firstName = clientData.client_name?.split(' ')[0] || 'there';

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutGrid },
    { id: 'tasks', label: 'Tasks', icon: ListTodo },
    { id: 'schedule', label: 'Schedule', icon: CalendarPlus },
    { id: 'files', label: 'Files', icon: FolderOpen },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-full shadow-lg ${
              toast.type === 'error' ? 'bg-red-500' : 'bg-gradient-to-r from-green-500 to-emerald-500'
            } text-white font-medium`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-200/50">
                {clientData.business_name?.charAt(0) || 'B'}
              </div>
              <div className="hidden sm:block">
                <h1 className="font-bold text-gray-900">{clientData.business_name}</h1>
                <p className="text-xs text-gray-500">{clientData.client_id}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleMessagePM}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200/50"
              >
                <Mail size={16} /> Contact PM
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <LogOut size={20} />
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Clean & Light */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Greeting & Progress */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-gray-500 text-sm mb-1">Welcome back</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Hey {firstName}! <motion.span 
                  animate={{ rotate: [0, 14, -8, 14, -4, 10, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 1 }}
                  className="inline-block origin-bottom-right"
                >👋</motion.span>
              </h2>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex items-center gap-6"
            >
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">Project Progress</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  {progressPercent}%
                </p>
              </div>
              <div className="w-32 sm:w-48">
                <ProgressBar progress={progressPercent} />
              </div>
            </motion.div>
          </div>

          {/* Stage Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Desktop Timeline */}
            <div className="hidden md:block">
              <StageTimeline currentStage={currentStage} />
            </div>
            {/* Mobile Timeline */}
            <div className="md:hidden">
              <MobileStageTimeline currentStage={currentStage} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="sticky top-16 z-30 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1 overflow-x-auto py-3 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id 
                    ? 'bg-white text-indigo-600 shadow-md border border-gray-200' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Bento Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <BentoCard 
                  icon={ListTodo} 
                  label="Open Tasks" 
                  value={openTasks}
                  gradient="linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)"
                  delay={0.1}
                />
                <BentoCard 
                  icon={CheckCircle2} 
                  label="Completed" 
                  value={completedTasks}
                  gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
                  delay={0.2}
                />
                <BentoCard 
                  icon={Target} 
                  label="Milestones" 
                  value={`${milestonesDone}/${milestonesTotal}`}
                  gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                  delay={0.3}
                />
                <BentoCard 
                  icon={FolderOpen} 
                  label="Files" 
                  value={filesCount}
                  gradient="linear-gradient(135deg, #EC4899 0%, #DB2777 100%)"
                  delay={0.4}
                />
              </div>

              {/* Two Column */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Quick Actions */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Zap size={20} className="text-amber-500" />
                    Quick Actions
                  </h3>
                  <div className="space-y-3">
                    <ActionCard
                      icon={Upload}
                      title="Submit Assets"
                      description="Upload logos, photos, and content"
                      onClick={() => window.open(JOTFORM_ASSETS_URL, '_blank')}
                      gradient="linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)"
                      external
                    />
                    <ActionCard
                      icon={MessageCircle}
                      title="Weekly Check-in"
                      description="Share updates and feedback"
                      onClick={() => window.open(JOTFORM_CHECKIN_URL, '_blank')}
                      gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
                      external
                    />
                    <ActionCard
                      icon={Mail}
                      title="Message Your PM"
                      description="Get help from your project manager"
                      onClick={handleMessagePM}
                      gradient="linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)"
                    />
                  </div>
                </motion.div>

                {/* Tasks */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Award size={20} className="text-indigo-500" />
                      Your Tasks
                    </h3>
                    <button
                      onClick={() => setActiveTab('tasks')}
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                    >
                      View all <ArrowRight size={14} />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {todos.filter(t => t.status !== 'completed').slice(0, 4).map((todo, index) => (
                      <TodoItem key={todo.id} todo={todo} onToggle={onToggleTodo} index={index} />
                    ))}
                    {openTasks === 0 && (
                      <div className="text-center py-8">
                        <motion.div
                          animate={{ rotate: [0, 10, -10, 0] }}
                          transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                          className="text-4xl mb-2"
                        >
                          🎉
                        </motion.div>
                        <p className="text-gray-500 font-medium">All tasks completed!</p>
                        <p className="text-gray-400 text-sm">You're crushing it</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Project Manager Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-6 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 relative overflow-hidden"
              >
                <div className="absolute inset-0 opacity-10" style={{
                  backgroundImage: `radial-gradient(circle at 30% 50%, white 1px, transparent 1px)`,
                  backgroundSize: '24px 24px'
                }} />
                <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-white font-bold text-xl">
                    {clientData.agent?.name?.charAt(0) || 'B'}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white text-lg">{clientData.agent?.name || 'BM-Assist Team'}</h4>
                    <p className="text-white/80 text-sm">{clientData.agent?.role || 'Project Manager'}</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleMessagePM}
                    className="w-full sm:w-auto px-6 py-3 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-white/90 transition-colors flex items-center justify-center gap-2 shadow-lg"
                  >
                    <Send size={18} />
                    Send Message
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {activeTab === 'tasks' && (
            <motion.div
              key="tasks"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6">Your Tasks</h3>
              
              {openTasks > 0 && (
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                    Open ({openTasks})
                  </h4>
                  <div className="space-y-2">
                    {todos.filter(t => t.status !== 'completed').map((todo, index) => (
                      <TodoItem key={todo.id} todo={todo} onToggle={onToggleTodo} index={index} />
                    ))}
                  </div>
                </div>
              )}

              {completedTasks > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                    Completed ({completedTasks})
                  </h4>
                  <div className="space-y-2">
                    {todos.filter(t => t.status === 'completed').map((todo, index) => (
                      <TodoItem key={todo.id} todo={todo} onToggle={onToggleTodo} index={index} />
                    ))}
                  </div>
                </div>
              )}

              {todos.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-5xl mb-4">📋</div>
                  <p className="text-gray-500 font-medium">No tasks yet</p>
                  <p className="text-gray-400 text-sm">Tasks will appear here as your project progresses</p>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'schedule' && (
            <motion.div
              key="schedule"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Schedule Meeting Card */}
              <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{
                  backgroundImage: `radial-gradient(circle at 30% 50%, white 1px, transparent 1px)`,
                  backgroundSize: '24px 24px'
                }} />
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">Schedule a Meeting</h3>
                      <p className="text-white/80 text-sm">Book time with your project manager</p>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowScheduleModal(true)}
                      className="w-full sm:w-auto px-6 py-3 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-white/90 transition-colors flex items-center justify-center gap-2 shadow-lg"
                    >
                      <CalendarPlus size={18} />
                      Request Meeting
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Upcoming Meetings */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Your Meetings</h3>
                
                {loadingMeetings ? (
                  <div className="text-center py-8 text-gray-500">Loading meetings...</div>
                ) : meetings.length > 0 ? (
                  <div className="space-y-3">
                    {meetings.map((meeting, index) => (
                      <motion.div
                        key={meeting.id || index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              meeting.status === 'confirmed' 
                                ? 'bg-green-100 text-green-600' 
                                : meeting.status === 'cancelled'
                                  ? 'bg-red-100 text-red-600'
                                  : 'bg-amber-100 text-amber-600'
                            }`}>
                              {meeting.meeting_type === 'video' ? <Video size={18} /> : <Calendar size={18} />}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{meeting.title || 'Meeting'}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(meeting.date).toLocaleDateString('en-US', { 
                                  weekday: 'short', month: 'short', day: 'numeric' 
                                })} at {meeting.time}
                              </p>
                              {meeting.notes && (
                                <p className="text-xs text-gray-400 mt-1">{meeting.notes}</p>
                              )}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            meeting.status === 'confirmed' 
                              ? 'bg-green-100 text-green-700' 
                              : meeting.status === 'cancelled'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-700'
                          }`}>
                            {meeting.status === 'confirmed' ? 'Confirmed' : 
                             meeting.status === 'cancelled' ? 'Cancelled' : 'Pending'}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-5xl mb-4">📅</div>
                    <p className="text-gray-500 font-medium mb-2">No meetings scheduled</p>
                    <p className="text-gray-400 text-sm">Request a meeting with your project manager above</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Schedule Meeting Modal */}
          <AnimatePresence>
            {showScheduleModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                onClick={() => setShowScheduleModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-white">Schedule Check-in</h3>
                        <p className="text-white/80 text-sm">30-minute video call</p>
                      </div>
                      <button 
                        onClick={() => setShowScheduleModal(false)}
                        className="text-white/80 hover:text-white"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>
                  
                  <form onSubmit={handleSubmitMeeting} className="p-6 space-y-5">
                    {/* PM Info - Read Only */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Your Project Manager</p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {clientData.agent?.name?.charAt(0) || 'B'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{clientData.agent?.name || 'BM-Assist Team'}</p>
                          <p className="text-sm text-gray-500">{clientData.agent?.email || 'intake@bm-assist.co'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Date Picker */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Select a Date</label>
                      <input
                        type="date"
                        required
                        value={selectedDate}
                        onChange={e => handleDateChange(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                      />
                    </div>

                    {/* Available Time Slots */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {selectedDate ? 'Select a Time' : 'Available Times'}
                      </label>
                      
                      {!selectedDate ? (
                        <p className="text-gray-400 text-sm py-4 text-center">Please select a date first</p>
                      ) : loadingSlots ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="w-6 h-6 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                        </div>
                      ) : availableSlots.length === 0 ? (
                        <div className="text-center py-6 bg-gray-50 rounded-xl">
                          <p className="text-gray-500 font-medium">No available times</p>
                          <p className="text-gray-400 text-sm">Try selecting a different day</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                          {availableSlots.map(slot => {
                            const hour = parseInt(slot.split(':')[0]);
                            const minute = slot.split(':')[1];
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                            const displayTime = `${displayHour}:${minute} ${ampm}`;
                            
                            return (
                              <button
                                key={slot}
                                type="button"
                                onClick={() => setSelectedTime(slot)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                  selectedTime === slot
                                    ? 'bg-indigo-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600'
                                }`}
                              >
                                {displayTime}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Notes Field */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Notes <span className="font-normal text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        value={meetingNotes}
                        onChange={e => setMeetingNotes(e.target.value)}
                        placeholder="What would you like to discuss? Any questions or updates?"
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-gray-700"
                      />
                    </div>

                    {/* Selected Summary */}
                    {selectedDate && selectedTime && (
                      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                        <p className="text-sm text-indigo-600 font-medium">Your Check-in Meeting:</p>
                        <p className="text-indigo-900 font-semibold mt-1">
                          {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                            weekday: 'long', month: 'long', day: 'numeric' 
                          })} at {(() => {
                            const hour = parseInt(selectedTime.split(':')[0]);
                            const minute = selectedTime.split(':')[1];
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                            return `${displayHour}:${minute} ${ampm}`;
                          })()}
                        </p>
                        <p className="text-sm text-indigo-600 mt-1">with {clientData.agent?.name || 'BM-Assist Team'}</p>
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowScheduleModal(false);
                          setSelectedDate('');
                          setSelectedTime('');
                          setMeetingNotes('');
                          setAvailableSlots([]);
                        }}
                        className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submittingMeeting || !selectedDate || !selectedTime}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {submittingMeeting ? 'Booking...' : 'Book Meeting'}
                      </button>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {activeTab === 'files' && (
            <motion.div
              key="files"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* File Upload Section */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Upload Files</h3>
                <FileUpload 
                  clientId={clientData.client_id}
                  onUploadComplete={fetchClientData}
                  uploaderRole="client"
                  uploaderName={clientData.client_name || 'Client'}
                />
              </div>

              {/* Files List */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Project Files ({clientData.files?.length || 0})</h3>
                {clientData.files?.length > 0 ? (
                  <FilesList files={clientData.files} onRefresh={fetchClientData} />
                ) : (
                  <div className="text-center py-12">
                    <div className="text-5xl mb-4">📁</div>
                    <p className="text-gray-500 font-medium mb-2">No files yet</p>
                    <p className="text-gray-400 text-sm">Upload files above or they'll appear here when shared</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'messages' && (
            <motion.div
              key="messages"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
                <h3 className="text-xl font-bold text-gray-900">Messages</h3>
                <p className="text-sm text-gray-500 mt-1">Chat with your project team</p>
              </div>
              <div className="h-[500px]">
                <SimpleChatThread 
                  clientId={clientData.client_id}
                  currentUser="client"
                  clientName={clientData.client_name}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <InvoiceModal
          invoiceUrl={clientData.invoice_url}
          onClose={() => setShowInvoiceModal(false)}
        />
      )}
    </div>
  );
};

export default ClientDashboardPageV4;
